import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useJobCompletion } from 'lib/hooks/useJobCompletion';
import {
  currentRefreshStatusAtom,
  fetchRefreshStatus,
  latestUnitWellAssignmentsAtom,
  refreshOutcomeAtom,
  refreshRequestAtom,
  refreshStatusAtom,
  rowsReloadTickAtom,
} from './atoms';

// The refresh job pushes this when it ends (Unit::WellAssignment::SegmentShare
// REFRESH_CHANNEL / REFRESH_EVENT). A hint only: the page then re-reads the
// status, which is what it acts on.
export const REFRESH_CHANNEL = 'unit_well_assignment_segment_shares';
export const REFRESH_EVENT = 'refresh_finished';

// The backstop for a push that never arrives. A refresh takes about a minute;
// past 15 the page stops waiting and says so.
export const REFRESH_POLL = {
  delayMs: 5_000,
  intervalMs: 10_000,
  maxMs: 15 * 60_000,
};

export type RefreshState = 'idle' | 'queued' | 'refreshing';

// The refresh as the page shows it: 'refreshing' while the server reports one
// running, 'queued' after "Refresh now" was accepted and before it ends,
// otherwise 'idle'.
//
// While either holds, the page listens for the job's push and polls the status
// alone (never the rows) on a bounded backstop. A request ends when the server's
// lastRefreshedAt moves on (landed) or its lastFailedAt does (failed, with the
// recorded reason). The rows are re-read once, when the status reports a newer
// refresh than they were read with.
export const useRefreshState = (): {
  state: RefreshState;
  lastRefreshedAt: string | null | undefined;
} => {
  const rows = useAtomValue(latestUnitWellAssignmentsAtom);
  const status = useAtomValue(currentRefreshStatusAtom);
  const setStatus = useSetAtom(refreshStatusAtom);
  const [request, setRequest] = useAtom(refreshRequestAtom);
  const setOutcome = useSetAtom(refreshOutcomeAtom);
  const reloadRows = useSetAtom(rowsReloadTickAtom);
  const reloadedFor = useRef<string | null>(null);

  const refreshing = status?.refreshing ?? false;
  const waiting = request !== undefined || refreshing;

  useJobCompletion({
    channel: waiting ? REFRESH_CHANNEL : null,
    events: [REFRESH_EVENT],
    reconcile: async () => {
      try {
        setStatus(await fetchRefreshStatus());
      } catch {
        // A failed read decides nothing; the next trigger reads again.
      }
    },
    poll: waiting
      ? {
          ...REFRESH_POLL,
          onExpired: () => {
            if (!request) return;
            setRequest(undefined);
            setOutcome({ kind: 'no_result' });
          },
        }
      : null,
  });

  // Ends a request once the server's status has moved on from where it was.
  useEffect(() => {
    if (!request || !status) return;
    if (status.lastFailedAt !== request.fromFailedAt) {
      setRequest(undefined);
      setOutcome({ kind: 'failed', message: status.lastFailure });
    } else if (
      !status.refreshing &&
      status.lastRefreshedAt !== request.fromRefreshedAt
    ) {
      setRequest(undefined);
      setOutcome(null);
    }
  }, [request, status, setRequest, setOutcome]);

  // Re-reads the rows once a refresh newer than theirs has landed.
  const rowsRefreshedAt = rows?.refresh.lastRefreshedAt;
  const landedAt = status?.lastRefreshedAt ?? null;
  useEffect(() => {
    if (!rows || refreshing || landedAt === null) return;
    if (landedAt === rowsRefreshedAt || reloadedFor.current === landedAt)
      return;
    reloadedFor.current = landedAt;
    reloadRows((tick) => tick + 1);
  }, [rows, refreshing, landedAt, rowsRefreshedAt, reloadRows]);

  let state: RefreshState = 'idle';
  if (refreshing) state = 'refreshing';
  else if (request) state = 'queued';
  return { state, lastRefreshedAt: status?.lastRefreshedAt };
};
